import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@munawb/hooks';
import { supabase } from '@munawb/supabase';
import type { ProfileRow } from '@munawb/types';
import { USER_ROLE_VALUES } from '@munawb/types';
import { formatDate } from '../lib/utils';

type TabId = 'pending-users' | 'users' | 'departments' | 'settings';

type PendingReg = {
  id:      string;
  name:    string;
  email:   string;
  phone:   string | null;
  title:   string | null;
  dept_id: string | null;
  status:  string;
  created_at: string;
  departments: { name: string } | null;
};

export default function AdminPage() {
  const { profile } = useAuthStore();
  const qc          = useQueryClient();
  const [activeTab, setActiveTab]       = useState<TabId>('pending-users');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);

  // Pending registrations
  const { data: pendingRegs = [], isLoading: pendingLoading } = useQuery<PendingReg[]>({
    queryKey: ['pending-registrations'],
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('pending_registrations')
        .select('*, departments(name)')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as PendingReg[];
    },
  });

  // All profiles
  const { data: users = [], isLoading: usersLoading } = useQuery<(ProfileRow & { departments: { name: string } | null })[]>({
    queryKey: ['all-profiles'],
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*, departments(name)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as (ProfileRow & { departments: { name: string } | null })[];
    },
    enabled: activeTab === 'users',
  });

  // Departments
  const { data: departments = [] } = useQuery<{ id: string; name: string; active: boolean }[]>({
    queryKey: ['departments'],
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('departments')
        .select('id, name, active')
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
    enabled: activeTab === 'departments',
  });

  // System settings
  const { data: settings = [] } = useQuery<{ key: string; label: string; value: string | null }[]>({
    queryKey: ['system-settings'],
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, label, value')
        .order('key');
      if (error) throw error;
      return (data ?? []) as { key: string; label: string; value: string | null }[];
    },
    enabled: activeTab === 'settings',
  });

  // Approve registration
  const { mutateAsync: approveReg, isPending: approving } = useMutation({
    mutationFn: async (token: string) => {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token ?? ''}`,
          },
          body: JSON.stringify({ token, action: 'approve' }),
        }
      );
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error ?? 'فشل في قبول الطلب');
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-registrations'] }),
  });

  // Reject registration
  const { mutateAsync: rejectReg, isPending: rejecting } = useMutation({
    mutationFn: async ({ token, reason }: { token: string; reason: string }) => {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/auth-approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token ?? ''}`,
          },
          body: JSON.stringify({ token, action: 'reject', rejection_reason: reason }),
        }
      );
      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.error ?? 'فشل في رفض الطلب');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pending-registrations'] });
      setRejectTarget(null);
      setRejectReason('');
    },
  });

  // Update user role
  const { mutateAsync: updateUserRole } = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase
        .from('profiles')
        .update({ role })
        .eq('id', userId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['all-profiles'] }),
  });

  // Update user status
  const { mutateAsync: updateUserStatus } = useMutation({
    mutationFn: async ({ userId, status }: { userId: string; status: string }) => {
      const { error } = await supabase
        .from('profiles')
        .update({ status })
        .eq('id', userId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['all-profiles'] }),
  });

  // Save setting
  const { mutateAsync: saveSetting } = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await supabase
        .from('system_settings')
        .update({ value, updated_at: new Date().toISOString(), updated_by: profile?.id })
        .eq('key', key);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system-settings'] }),
  });

  // Add department
  const [newDeptName, setNewDeptName] = useState('');
  const { mutateAsync: addDept } = useMutation({
    mutationFn: async (name: string) => {
      const { error } = await supabase.from('departments').insert({ name });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments'] });
      setNewDeptName('');
    },
  });

  const handleSignOut = async () => { await supabase.auth.signOut(); };

  const TABS: { id: TabId; label: string; badge?: number }[] = [
    { id: 'pending-users', label: 'طلبات التسجيل', badge: pendingRegs.length },
    { id: 'users',         label: 'المستخدمون' },
    { id: 'departments',   label: 'الأقسام' },
    { id: 'settings',      label: 'الإعدادات' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-blue-950">

      {/* Header */}
      <header className="glass-panel border-b border-slate-200/80 dark:border-slate-700/50 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/munawb-logo.png" alt="" className="w-8 h-8 rounded-xl" />
            <span className="font-black text-slate-800 dark:text-white text-sm">مُناوِب — لوحة المدير</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-slate-600 dark:text-slate-400">{profile?.name}</span>
            <button onClick={handleSignOut} className="btn-secondary text-xs px-3 py-1.5">خروج</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap mb-6">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative px-5 py-2.5 rounded-xl font-black text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50'
              }`}
            >
              {tab.label}
              {tab.badge != null && tab.badge > 0 && (
                <span className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-red-500 rounded-full text-white text-[10px] font-black flex items-center justify-center">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Pending Users ─── */}
        {activeTab === 'pending-users' && (
          <div className="space-y-3">
            {pendingLoading ? (
              <div className="glass-panel rounded-2xl p-10 text-center">
                <span className="spinner border-blue-200 border-t-blue-600" />
              </div>
            ) : pendingRegs.length === 0 ? (
              <div className="glass-panel rounded-2xl p-10 text-center">
                <p className="text-slate-400 font-bold text-sm">لا توجد طلبات معلقة</p>
              </div>
            ) : (
              pendingRegs.map(reg => (
                <div key={reg.id} className="glass-panel rounded-2xl p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <p className="font-black text-slate-800 dark:text-white">{reg.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5" dir="ltr">{reg.email}</p>
                      {reg.phone && <p className="text-xs text-slate-400 mt-0.5" dir="ltr">{reg.phone}</p>}
                      {reg.title && <p className="text-xs text-slate-400 mt-0.5">{reg.title}</p>}
                      {reg.departments && <p className="text-xs text-blue-600 dark:text-blue-400 font-bold mt-0.5">{reg.departments.name}</p>}
                      <p className="text-[11px] text-slate-400 mt-1">{formatDate(reg.created_at)}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setRejectTarget(reg.id); }}
                        className="btn-danger text-xs px-3 py-1.5"
                        disabled={rejecting}
                      >
                        رفض
                      </button>
                      <button
                        onClick={async () => {
                          // We need the token — fetch it
                          const { data } = await supabase
                            .from('pending_registrations')
                            .select('token')
                            .eq('id', reg.id)
                            .single();
                          if (data?.token) await approveReg(data.token);
                        }}
                        className="btn-primary text-xs px-3 py-1.5"
                        style={{ background: '#16a34a' }}
                        disabled={approving}
                      >
                        {approving ? <span className="spinner border-white/30 border-t-white" /> : 'قبول'}
                      </button>
                    </div>
                  </div>
                  {/* Reject form */}
                  {rejectTarget === reg.id && (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-2">
                      <input
                        type="text"
                        className="input-field text-sm"
                        placeholder="سبب الرفض (اختياري)"
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => setRejectTarget(null)}
                          className="btn-secondary text-xs flex-1"
                        >
                          إلغاء
                        </button>
                        <button
                          onClick={async () => {
                            const { data } = await supabase
                              .from('pending_registrations')
                              .select('token')
                              .eq('id', reg.id)
                              .single();
                            if (data?.token) await rejectReg({ token: data.token, reason: rejectReason });
                          }}
                          className="btn-danger text-xs flex-1"
                          disabled={rejecting}
                        >
                          {rejecting ? <span className="spinner border-white/30 border-t-white" /> : 'تأكيد الرفض'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* ── Users ─── */}
        {activeTab === 'users' && (
          <div className="glass-panel rounded-2xl overflow-hidden">
            {usersLoading ? (
              <div className="p-10 text-center"><span className="spinner border-blue-200 border-t-blue-600" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/50">
                    <tr>
                      {['الاسم', 'القسم', 'الدور', 'الحالة', 'تاريخ الإنشاء', ''].map(h => (
                        <th key={h} className="px-4 py-3 text-right font-black text-xs text-slate-500 dark:text-slate-400">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                        <td className="px-4 py-3 font-bold text-slate-800 dark:text-white">{u.name}</td>
                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{u.departments?.name ?? '—'}</td>
                        <td className="px-4 py-3">
                          <select
                            className="text-xs font-bold bg-transparent border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1"
                            value={u.role}
                            onChange={e => updateUserRole({ userId: u.id, role: e.target.value })}
                            disabled={u.id === profile?.id}
                          >
                            {USER_ROLE_VALUES.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            className="text-xs font-bold bg-transparent border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1"
                            value={u.status}
                            onChange={e => updateUserStatus({ userId: u.id, status: e.target.value })}
                            disabled={u.id === profile?.id}
                          >
                            {['active', 'pending', 'suspended', 'rejected'].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400">{formatDate(u.created_at)}</td>
                        <td className="px-4 py-3" />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Departments ─── */}
        {activeTab === 'departments' && (
          <div className="space-y-4">
            <div className="glass-panel rounded-2xl p-5">
              <h3 className="text-sm font-black text-slate-800 dark:text-white mb-3">إضافة قسم جديد</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  className="input-field flex-1"
                  placeholder="اسم القسم"
                  value={newDeptName}
                  onChange={e => setNewDeptName(e.target.value)}
                />
                <button
                  onClick={() => newDeptName.trim() && addDept(newDeptName.trim())}
                  className="btn-primary"
                >
                  إضافة
                </button>
              </div>
            </div>
            <div className="glass-panel rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="px-4 py-3 text-right font-black text-xs text-slate-500 dark:text-slate-400">اسم القسم</th>
                    <th className="px-4 py-3 text-right font-black text-xs text-slate-500 dark:text-slate-400">الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {departments.map(d => (
                    <tr key={d.id} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-4 py-3 font-bold text-slate-800 dark:text-white">{d.name}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-black px-2.5 py-1 rounded-full ${
                          d.active
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-500'
                        }`}>
                          {d.active ? 'نشط' : 'معطّل'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Settings ─── */}
        {activeTab === 'settings' && (
          <div className="space-y-3">
            {settings.map(setting => (
              <SettingRow
                key={setting.key}
                label={setting.label}
                value={setting.value ?? ''}
                onSave={val => saveSetting({ key: setting.key, value: val })}
              />
            ))}
          </div>
        )}

      </main>
    </div>
  );
}

/* ── SettingRow ─────────────────────────────────────────────────── */

function SettingRow({ label, value, onSave }: { label: string; value: string; onSave: (v: string) => Promise<void> }) {
  const [val, setVal]       = useState(value);
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(val);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="glass-panel rounded-2xl p-4 flex items-center gap-4">
      <div className="flex-1">
        <label className="block text-xs font-black text-slate-500 dark:text-slate-400 mb-1.5">{label}</label>
        <input
          type="text"
          className="input-field text-sm"
          value={val}
          onChange={e => setVal(e.target.value)}
          dir="ltr"
        />
      </div>
      <button
        onClick={handleSave}
        disabled={saving || val === value}
        className={`shrink-0 px-4 py-2 rounded-xl font-black text-xs transition-all ${
          saved
            ? 'bg-green-600 text-white'
            : 'bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50'
        }`}
      >
        {saving ? <span className="spinner border-white/30 border-t-white w-4 h-4" /> : saved ? '✓ تم' : 'حفظ'}
      </button>
    </div>
  );
}
